/**
 * Раздел «Ресурсы» (6.3 ТЗ): загрузка людей и техники из графика и норм по неделям против наличия
 * на вахте и плана мобилизации, стоимость по неделям, справочник ресурсов и ставок с версиями,
 * персонал с допусками, вахты, табель с согласованием, план найма и мобилизации.
 */
import {
  can,
  canCompanyWide,
  type LoadView,
  type MobilizationRow,
  RESOURCE_TYPE_TITLES,
  RESOURCE_TYPES,
  type ResourceRow,
  type ResourceType,
  type RotationRow,
  seesMoney,
  type TimesheetView,
  type ToolUnit,
  WORKER_DOC_KINDS,
  WORKER_DOC_TITLES,
  type WorkerRow,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import { Plus, Trash2 } from 'lucide-react';
import { type FormEvent, useMemo, useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { tenge } from '../../lib/format';
import { projectsQuery } from '../../lib/projects';
import { useMe } from '../../lib/session';
import { Button, IconButton } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Input, Select } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { LField } from '../schedule/LField';
import { CampPanel } from './CampPanel';
import { DemandPanel } from './DemandPanel';
import { MachinesPanel } from './MachinesPanel';
import { RotationGridPanel } from './RotationGrid';

type Tab = 'load' | 'dict' | 'people' | 'machines' | 'rotations' | 'camp' | 'timesheet' | 'mobilization';
const TABS: Array<{ key: Tab; title: string; project?: boolean }> = [
  { key: 'load', title: 'Загрузка' },
  { key: 'people', title: 'Персонал' },
  { key: 'machines', title: 'Техника' },
  { key: 'rotations', title: 'Вахты', project: true },
  { key: 'camp', title: 'Вахтовый посёлок', project: true },
  { key: 'timesheet', title: 'Табель', project: true },
  { key: 'mobilization', title: 'Мобилизация', project: true },
  { key: 'dict', title: 'Справочник и ставки' },
];
const d2 = (s: string | null | undefined) => (s ? s.slice(0, 10).split('-').reverse().join('.') : '—');
const num = (s: string) => {
  const v = Number(s.replace(/\s/g, '').replace(',', '.'));
  return s.trim() === '' || !Number.isFinite(v) ? null : v;
};

export function ResourcesSectionPage() {
  const search = useSearch({ strict: false }) as { tab?: Tab; project?: string };
  const navigate = useNavigate();
  const me = useMe();
  const tab: Tab = TABS.some((t) => t.key === search.tab) ? (search.tab as Tab) : 'load';
  const projects = useQuery(projectsQuery('active'));
  const visible = (projects.data ?? []).filter((p) =>
    can(me.access, 'resources.view', {
      legalEntityId: p.legalEntityId,
      directionId: p.directionId,
      projectId: p.id,
      projectPath: [p.parentId],
    }),
  );
  const projectId = search.project && visible.some((p) => p.id === search.project) ? search.project : '';
  const project = visible.find((p) => p.id === projectId);
  const set = (p: Partial<{ tab: Tab; project: string }>) =>
    navigate({
      to: '/resources',
      search: { tab: p.tab ?? tab, project: (p.project ?? projectId) || undefined } as never,
    });
  useCrumbs([
    ...(project ? [{ label: project.code, title: project.name }] : []),
    { label: TABS.find((t) => t.key === tab)!.title },
  ]);
  const needProject = TABS.find((t) => t.key === tab)?.project;
  return (
    <Page>
      <PageHeader
        title="Ресурсы"
        subtitle="Люди и техника из графика и норм, вахты, табели, допуски, загрузка и стоимость"
      />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <LField label="Проект" className="w-full sm:w-[360px]">
          <Select
            value={projectId}
            onChange={(e) => set({ project: e.target.value })}
            className="max-md:h-11"
          >
            <option value="">
              {tab === 'load' || tab === 'people' || tab === 'dict' || tab === 'machines'
                ? 'Все проекты'
                : 'Выберите проект'}
            </option>
            {visible.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} · {p.siteName ? `${p.siteName}. ` : ''}
                {p.name}
              </option>
            ))}
          </Select>
        </LField>
      </div>
      <div className="mb-4 flex gap-1 overflow-x-auto border-b border-line" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => set({ tab: t.key })}
            className={clsx(
              'relative min-h-11 shrink-0 px-3 text-[14px] font-medium whitespace-nowrap',
              tab === t.key ? 'text-ink' : 'text-ink-2 hover:text-ink',
            )}
          >
            {t.title}
            {tab === t.key ? <span className="absolute inset-x-2 -bottom-px h-0.5 bg-ink" /> : null}
          </button>
        ))}
      </div>
      {needProject && !projectId ? (
        <EmptyState title="Выберите проект">Вахты, табель и план мобилизации ведутся по проекту.</EmptyState>
      ) : tab === 'load' ? (
        <LoadTab projectId={projectId} />
      ) : tab === 'dict' ? (
        <DictPanel />
      ) : tab === 'people' ? (
        <PeoplePanel projectId={projectId} />
      ) : tab === 'machines' ? (
        <MachinesPanel
          projectId={projectId}
          projects={visible.map((p) => ({ id: p.id, code: p.code }))}
          canEdit={
            project ? projectCan(me, project, 'resources.edit') : canCompanyWide(me.access, 'resources.edit')
          }
        />
      ) : tab === 'rotations' ? (
        <div className="flex flex-col gap-4">
          <RotationGridPanel projectId={projectId} canEdit={projectCan(me, project, 'resources.edit')} />
          <RotationsPanel projectId={projectId} canEdit={projectCan(me, project, 'resources.edit')} />
        </div>
      ) : tab === 'camp' ? (
        <CampPanel projectId={projectId} canEdit={projectCan(me, project, 'resources.edit')} />
      ) : tab === 'timesheet' ? (
        <TimesheetPanel key={projectId} projectId={projectId} />
      ) : (
        <MobilizationPanel projectId={projectId} canEdit={projectCan(me, project, 'resources.edit')} />
      )}
    </Page>
  );
}

function projectCan(
  me: ReturnType<typeof useMe>,
  p: { id: string; legalEntityId: string; directionId: string | null; parentId: string | null } | undefined,
  perm: string,
) {
  return p
    ? can(me.access, perm, {
        legalEntityId: p.legalEntityId,
        directionId: p.directionId,
        projectId: p.id,
        projectPath: [p.parentId],
      })
    : false;
}

const resourcesQuery = {
  queryKey: ['resources'],
  queryFn: () => api<ResourceRow[]>('/resources', { query: { all: true } }),
};

/* ───────────────────────── Загрузка ───────────────────────── */

/** «Загрузка»: по неделям (все проекты или проект) и по дням против факта площадки — только у проекта */
function LoadTab({ projectId }: { projectId: string }) {
  const [mode, setMode] = useState<'weeks' | 'days'>('weeks');
  return (
    <div className="flex flex-col gap-4">
      {projectId ? (
        <fieldset className="flex gap-2">
          <legend className="sr-only">Вид загрузки</legend>
          {(
            [
              ['weeks', 'По неделям'],
              ['days', 'По дням: план и факт'],
            ] as const
          ).map(([k, t]) => (
            <Button
              key={k}
              size="sm"
              variant={mode === k ? 'primary' : 'ghost'}
              aria-pressed={mode === k}
              onClick={() => setMode(k)}
              className="max-md:h-11"
            >
              {t}
            </Button>
          ))}
        </fieldset>
      ) : null}
      {projectId && mode === 'days' ? (
        <DemandPanel projectId={projectId} />
      ) : (
        <LoadPanel projectId={projectId} />
      )}
    </div>
  );
}

function LoadPanel({ projectId }: { projectId: string }) {
  const q = useQuery({
    queryKey: ['resource-load', projectId || 'all'],
    queryFn: () => api<LoadView>(projectId ? `/projects/${projectId}/resource-load` : '/resource-load'),
  });
  const [kind, setKind] = useState<'all' | ResourceType>('all');
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const v = q.data;
  if (!v.rows.length)
    return (
      <EmptyState title="Назначений пока нет">
        Потребность считается из назначений людей и техники на работы графика: откройте работу в графике и
        добавьте ресурсы.
      </EmptyState>
    );
  const rows = v.rows.filter((r) => kind === 'all' || r.kind === kind);
  const over = (r: LoadView['rows'][number], i: number) =>
    r.available !== null && r.need[i]! > r.available[i]! + (r.planned ? 0 : 0);
  const overloaded = v.rows.filter((r) => r.need.some((_, i) => over(r, i))).length;
  return (
    <div className="flex flex-col gap-4">
      {v.missingRates.length ? (
        <div className="rounded-lg border border-warning/40 bg-warning-soft px-4 py-3 text-[14px]">
          Нет ставок на даты работ: {v.missingRates.join(', ')}. Стоимость по ним не считается — внесите
          ставки до утверждения себестоимости.
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-[220px]">
          <Select
            value={kind}
            onChange={(e) => setKind(e.target.value as typeof kind)}
            className="max-md:h-11"
            aria-label="Вид ресурсов"
          >
            <option value="all">Все ресурсы</option>
            {RESOURCE_TYPES.map((k) => (
              <option key={k} value={k}>
                {RESOURCE_TYPE_TITLES[k]}
              </option>
            ))}
          </Select>
        </div>
        {overloaded ? (
          <Badge tone="danger">не хватает людей: {overloaded}</Badge>
        ) : (
          <Badge tone="success">людей хватает</Badge>
        )}
        <span className="text-[13px] text-ink-2">
          В ячейке — пик людей или машин за неделю; красным — больше, чем на вахте.
        </span>
      </div>
      <Panel bodyClassName="p-0">
        <ScrollX>
          <table className="text-[13px]">
            <thead>
              <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                <th className="sticky left-0 z-10 min-w-[200px] bg-surface-2 py-2 pr-2 pl-4 font-medium">
                  Ресурс
                </th>
                {v.weeks.map((w) => (
                  <th key={w} className="num px-1.5 py-2 text-right font-medium whitespace-nowrap">
                    {w.slice(8, 10)}.{w.slice(5, 7)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.resourceId} className="border-t border-line">
                  <td className="sticky left-0 z-10 bg-surface py-1.5 pr-2 pl-4">
                    <div className="max-w-[240px] truncate" title={r.name}>
                      {r.name}
                    </div>
                    <div className="text-[11px] text-ink-2">
                      {RESOURCE_TYPE_TITLES[r.kind]} · всего{' '}
                      {Math.round(r.amount.reduce((s, x) => s + x, 0)).toLocaleString('ru-RU')} {r.unit}
                    </div>
                  </td>
                  {r.need.map((n, i) => {
                    const bad = over(r, i);
                    const val = r.kind === 'material' ? r.amount[i]! : n;
                    return (
                      <td
                        key={v.weeks[i]}
                        className={clsx(
                          'num px-1.5 py-1.5 text-right whitespace-nowrap',
                          bad ? 'bg-danger-soft font-semibold text-danger' : val ? '' : 'text-ink-3',
                        )}
                        title={`${r.amount[i]} ${r.unit}${r.available ? `; на вахте ${r.available[i]}` : ''}${r.planned ? `; по плану мобилизации ${r.planned[i]}` : ''}`}
                      >
                        {val ? (Math.round(val * 10) / 10).toLocaleString('ru-RU') : '·'}
                        {r.available && val ? (
                          <span className="block text-[10px] font-normal text-ink-2">
                            из {r.available[i]}
                          </span>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollX>
      </Panel>
      {v.cost ? <CostChart weeks={v.weeks} cost={v.cost} /> : null}
    </div>
  );
}

/** Стоимость ресурсов по неделям в бюджет-график (РС-12): ФОТ, техника, материалы */
function CostChart({ weeks, cost }: { weeks: string[]; cost: NonNullable<LoadView['cost']> }) {
  const totals = weeks.map((_, i) => cost.labor[i]! + cost.equipment[i]! + cost.material[i]!);
  const max = Math.max(1, ...totals);
  const H = 160;
  const bw = Math.max(6, Math.min(28, 900 / Math.max(1, weeks.length)));
  const series: Array<[keyof typeof cost, string, string]> = [
    ['labor', 'ФОТ', 'var(--color-ink)'],
    ['equipment', 'Техника', 'var(--color-ink-2)'],
    ['material', 'Материалы', 'var(--color-accent)'],
  ];
  const sum = (k: keyof typeof cost) => cost[k].reduce((s, x) => s + x, 0);
  return (
    <Panel
      title="Стоимость ресурсов по неделям"
      meta={`всего ${tenge(sum('labor') + sum('equipment') + sum('material'))}`}
    >
      <ScrollX stickyFirst={false}>
        <svg
          width={weeks.length * (bw + 4) + 10}
          height={H + 30}
          role="img"
          aria-label="Стоимость ресурсов по неделям"
        >
          {weeks.map((w, i) => {
            let y = H;
            return (
              <g key={w}>
                {series.map(([k, , color]) => {
                  const h = (cost[k][i]! / max) * H;
                  y -= h;
                  return h > 0 ? (
                    <rect key={k} x={5 + i * (bw + 4)} y={y} width={bw} height={h} fill={color}>
                      <title>{`${w.split('-').reverse().join('.')}: ${tenge(cost[k][i]!)}`}</title>
                    </rect>
                  ) : null;
                })}
                {i % Math.ceil(weeks.length / 12) === 0 ? (
                  <text x={5 + i * (bw + 4)} y={H + 16} fontSize={10} fill="var(--color-ink-2)">
                    {w.slice(8, 10)}.{w.slice(5, 7)}
                  </text>
                ) : null}
              </g>
            );
          })}
        </svg>
      </ScrollX>
      <div className="mt-2 flex flex-wrap gap-4 text-[13px]">
        {series.map(([k, label, color]) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span className="inline-block size-3 rounded-sm" style={{ background: color }} /> {label}:{' '}
            {tenge(sum(k))}
          </span>
        ))}
      </div>
    </Panel>
  );
}

/* ───────────────────────── Справочник ───────────────────────── */

function DictPanel() {
  const me = useMe();
  const q = useQuery(resourcesQuery);
  const canEdit = canCompanyWide(me.access, 'resources.edit');
  const money = seesMoney(me.access, 'acts');
  const [edit, setEdit] = useState<ResourceRow | 'new' | null>(null);
  const [kind, setKind] = useState<'all' | ResourceType>('all');
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const rows = q.data.filter((r) => kind === 'all' || r.kind === kind);
  const noRate = q.data.filter((r) => r.noRate && r.isActive);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        {canEdit ? (
          <Button
            variant="primary"
            icon={<Plus className="size-4" />}
            onClick={() => setEdit('new')}
            className="max-md:h-11"
          >
            Новый ресурс
          </Button>
        ) : null}
        <div className="w-[220px]">
          <Select
            value={kind}
            onChange={(e) => setKind(e.target.value as typeof kind)}
            className="max-md:h-11"
            aria-label="Вид ресурсов"
          >
            <option value="all">Все виды</option>
            {RESOURCE_TYPES.map((k) => (
              <option key={k} value={k}>
                {RESOURCE_TYPE_TITLES[k]}
              </option>
            ))}
          </Select>
        </div>
        {noRate.length ? <Badge tone="warning">без ставок: {noRate.length}</Badge> : null}
      </div>
      {!rows.length ? (
        <EmptyState title="Справочник пуст">
          Заведите профессии с разрядами, бригады, технику и материалы — их назначают на работы графика.
        </EmptyState>
      ) : (
        <Panel bodyClassName="p-0">
          <ul className="divide-y divide-line">
            {rows.map((r) => {
              const last = r.rates.at(-1);
              return (
                <li key={r.id}>
                  <button
                    type="button"
                    className="flex min-h-[52px] w-full flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-left hover:bg-surface-2"
                    onClick={() => setEdit(r)}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="text-[14px] font-medium">
                        {r.code ? <span className="mr-1 text-ink-2">{r.code}</span> : null}
                        {r.name}
                        {r.grade ? `, ${r.grade} разряд` : ''}
                        {!r.isActive ? <Badge className="ml-2">не используется</Badge> : null}
                      </span>
                      <span className="block text-[12px] text-ink-2">
                        {RESOURCE_TYPE_TITLES[r.kind]}
                        {r.equipmentType ? ` · ${r.equipmentType}` : ''}
                        {r.model ? ` ${r.model}` : ''}
                        {r.crew.length ? ` · состав: ${r.crew.length}` : ''}
                        {r.requiredDocs.length ? ` · нужны: ${r.requiredDocs.join(', ')}` : ''}
                      </span>
                    </span>
                    <span className="num text-[13px] text-ink-2">
                      {r.noRate ? (
                        <Badge tone="warning">нет ставки</Badge>
                      ) : money && last ? (
                        `${r.kind === 'material' ? `${tenge(last.perUnit)} за ${r.unit}` : last.perHour != null ? `${tenge(last.perHour)} в час` : `${tenge(last.perShift)} за смену`} с ${d2(last.validFrom)}`
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </Panel>
      )}
      {edit ? (
        <ResourceDialog
          res={edit === 'new' ? null : edit}
          all={q.data}
          canEdit={canEdit}
          money={money}
          onClose={() => setEdit(null)}
        />
      ) : null}
    </div>
  );
}

function ResourceDialog({
  res,
  all,
  canEdit,
  money,
  onClose,
}: {
  res: ResourceRow | null;
  all: ResourceRow[];
  canEdit: boolean;
  money: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    kind: res?.kind ?? ('labor' as ResourceType),
    code: res?.code ?? '',
    name: res?.name ?? '',
    grade: res?.grade != null ? String(res.grade) : '',
    equipmentType: res?.equipmentType ?? '',
    model: res?.model ?? '',
    unit: res?.unit ?? 'ч',
    requiredDocs: res?.requiredDocs.join(', ') ?? '',
    isActive: res?.isActive ?? true,
    crew: res?.crew ?? [],
  });
  const [rate, setRate] = useState({
    validFrom: new Date().toISOString().slice(0, 10),
    perHour: '',
    perShift: '',
    payrollPct: '',
    rotationPerDay: '',
    lodgingPerDay: '',
    mealsPerDay: '',
    travelPerTrip: '',
    ppePerMonth: '',
    perUnit: '',
  });
  const save = useMutation({
    mutationFn: () => {
      const body = {
        kind: f.kind,
        code: f.code.trim() || null,
        name: f.name.trim(),
        grade: f.kind === 'labor' && f.grade ? Number(f.grade) : null,
        equipmentType: f.kind === 'equipment' ? f.equipmentType.trim() || null : null,
        model: f.kind === 'equipment' ? f.model.trim() || null : null,
        unit: f.unit.trim() || 'ч',
        requiredDocs: f.requiredDocs
          .split(',')
          .map((x) => x.trim())
          .filter(Boolean),
        isActive: f.isActive,
        crew: f.kind === 'crew' ? f.crew : [],
      };
      return res
        ? api<ResourceRow[]>(`/resources/${res.id}`, { method: 'PATCH', body })
        : api<ResourceRow[]>('/resources', { body });
    },
    onSuccess: (rows) => {
      qc.setQueryData(resourcesQuery.queryKey, rows);
      toast(res ? 'Ресурс сохранён' : 'Ресурс добавлен');
      onClose();
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const addRate = useMutation({
    mutationFn: () =>
      api<ResourceRow[]>(`/resources/${res!.id}/rates`, {
        body: Object.fromEntries(Object.entries(rate).map(([k, v]) => [k, k === 'validFrom' ? v : num(v)])),
      }),
    onSuccess: (rows) => {
      qc.setQueryData(resourcesQuery.queryKey, rows);
      qc.invalidateQueries({ queryKey: ['resource-load'] });
      toast('Новая версия ставок сохранена');
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const cur = res ? all.find((x) => x.id === res.id) : undefined;
  const members = all.filter((x) => x.kind === 'labor' || x.kind === 'equipment');
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      width="lg"
      title={res ? res.name : 'Новый ресурс'}
      footer={
        <>
          <Button onClick={onClose} className="max-md:h-11">
            {canEdit ? 'Отмена' : 'Закрыть'}
          </Button>
          {canEdit ? (
            <Button
              variant="primary"
              loading={save.isPending}
              disabled={!f.name.trim()}
              onClick={() => save.mutate()}
              className="max-md:h-11"
            >
              Сохранить
            </Button>
          ) : null}
        </>
      }
    >
      <fieldset disabled={!canEdit} className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <LField label="Вид">
          <Select
            value={f.kind}
            disabled={Boolean(res)}
            onChange={(e) =>
              setF({
                ...f,
                kind: e.target.value as ResourceType,
                unit: e.target.value === 'material' ? 'шт' : 'ч',
              })
            }
            className="max-md:h-11"
          >
            {RESOURCE_TYPES.map((k) => (
              <option key={k} value={k}>
                {RESOURCE_TYPE_TITLES[k]}
              </option>
            ))}
          </Select>
        </LField>
        <LField label="Код">
          <Input
            value={f.code}
            onChange={(e) => setF({ ...f, code: e.target.value })}
            maxLength={40}
            className="max-md:h-11"
          />
        </LField>
        <LField label="Единица">
          <Input
            value={f.unit}
            onChange={(e) => setF({ ...f, unit: e.target.value })}
            maxLength={20}
            className="max-md:h-11"
          />
        </LField>
        <LField label="Название" className="sm:col-span-2">
          <Input
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
            maxLength={200}
            placeholder={f.kind === 'labor' ? 'Бетонщик' : f.kind === 'equipment' ? 'Экскаватор' : ''}
            className="max-md:h-11"
          />
        </LField>
        {f.kind === 'labor' ? (
          <LField label="Разряд">
            <Input
              inputMode="numeric"
              value={f.grade}
              onChange={(e) => setF({ ...f, grade: e.target.value })}
              className="max-md:h-11"
            />
          </LField>
        ) : f.kind === 'equipment' ? (
          <LField label="Тип и модель">
            <Input
              value={f.model}
              onChange={(e) => setF({ ...f, model: e.target.value })}
              placeholder="Hyundai R220"
              className="max-md:h-11"
            />
          </LField>
        ) : (
          <span />
        )}
        {f.kind === 'labor' ? (
          <LField
            label="Нужные документы через запятую"
            className="sm:col-span-3"
            hint="Без действующего документа человек не назначается на работу"
          >
            <Input
              value={f.requiredDocs}
              onChange={(e) => setF({ ...f, requiredDocs: e.target.value })}
              placeholder="Медосмотр, Удостоверение стропальщика"
              className="max-md:h-11"
            />
          </LField>
        ) : null}
        {f.kind === 'crew' ? (
          <div className="sm:col-span-3">
            <h3 className="mb-2 text-[14px] font-semibold">Состав</h3>
            {f.crew.map((c, i) => (
              <div key={c.resourceId} className="mb-2 flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-[14px]">
                  {all.find((x) => x.id === c.resourceId)?.name ?? '—'}
                </span>
                <Input
                  className="w-20 max-md:h-11"
                  inputMode="decimal"
                  aria-label="Количество"
                  value={String(c.qty)}
                  onChange={(e) =>
                    setF({
                      ...f,
                      crew: f.crew.map((x, k) => (k === i ? { ...x, qty: num(e.target.value) ?? 1 } : x)),
                    })
                  }
                />
                <IconButton
                  label="Убрать"
                  onClick={() => setF({ ...f, crew: f.crew.filter((_, k) => k !== i) })}
                  className="size-11 md:size-9"
                >
                  <Trash2 className="size-4" />
                </IconButton>
              </div>
            ))}
            <Select
              value=""
              onChange={(e) =>
                e.target.value && setF({ ...f, crew: [...f.crew, { resourceId: e.target.value, qty: 1 }] })
              }
              className="max-md:h-11"
              aria-label="Добавить в состав"
            >
              <option value="">Добавить профессию или технику…</option>
              {members
                .filter((m) => !f.crew.some((c) => c.resourceId === m.id))
                .map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                    {m.grade ? `, ${m.grade} р.` : ''}
                  </option>
                ))}
            </Select>
          </div>
        ) : null}
        {res ? (
          <label className="flex min-h-11 items-center gap-2 text-[14px]">
            <input
              type="checkbox"
              checked={f.isActive}
              onChange={(e) => setF({ ...f, isActive: e.target.checked })}
            />{' '}
            Используется
          </label>
        ) : null}
      </fieldset>
      {res && money ? (
        <section className="mt-6">
          <h3 className="mb-2 text-[14px] font-semibold">Ставки по версиям</h3>
          {cur?.rates.length ? (
            <ScrollX>
              <table className="w-full min-w-[620px] text-[13px]">
                <thead>
                  <tr className="text-left text-[12px] text-ink-2">
                    <th className="py-1 pr-2">С даты</th>
                    <th className="py-1 pr-2 text-right">
                      {res.kind === 'material' ? 'За единицу' : 'Час / смена'}
                    </th>
                    {res.kind === 'labor' ? (
                      <>
                        <th className="py-1 pr-2 text-right">ФОТ, %</th>
                        <th className="py-1 pr-2 text-right">Вахтовые, жильё, питание в день</th>
                        <th className="py-1 text-right">Дорога, СИЗ</th>
                      </>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {cur.rates.map((r) => (
                    <tr key={r.id} className="border-t border-line">
                      <td className="num py-1 pr-2">{d2(r.validFrom)}</td>
                      <td className="num py-1 pr-2 text-right">
                        {res.kind === 'material'
                          ? tenge(r.perUnit)
                          : `${tenge(r.perHour)} / ${tenge(r.perShift)}`}
                      </td>
                      {res.kind === 'labor' ? (
                        <>
                          <td className="num py-1 pr-2 text-right">{r.payrollPct ?? '—'}</td>
                          <td className="num py-1 pr-2 text-right">
                            {tenge(r.rotationPerDay)}, {tenge(r.lodgingPerDay)}, {tenge(r.mealsPerDay)}
                          </td>
                          <td className="num py-1 text-right">
                            {tenge(r.travelPerTrip)}, {tenge(r.ppePerMonth)}
                          </td>
                        </>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          ) : (
            <p className="text-[13px] text-ink-2">Ставок нет.</p>
          )}
          {canEdit ? (
            <form
              className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4"
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                addRate.mutate();
              }}
            >
              <LField label="Действует с">
                <Input
                  type="date"
                  value={rate.validFrom}
                  onChange={(e) => setRate({ ...rate, validFrom: e.target.value })}
                  required
                  className="max-md:h-11"
                />
              </LField>
              {res.kind === 'material' ? (
                <LField label="За единицу, ₸">
                  <Input
                    inputMode="decimal"
                    value={rate.perUnit}
                    onChange={(e) => setRate({ ...rate, perUnit: e.target.value })}
                    className="max-md:h-11"
                  />
                </LField>
              ) : (
                <>
                  <LField label="За час, ₸">
                    <Input
                      inputMode="decimal"
                      value={rate.perHour}
                      onChange={(e) => setRate({ ...rate, perHour: e.target.value })}
                      className="max-md:h-11"
                    />
                  </LField>
                  <LField label="За смену, ₸">
                    <Input
                      inputMode="decimal"
                      value={rate.perShift}
                      onChange={(e) => setRate({ ...rate, perShift: e.target.value })}
                      className="max-md:h-11"
                    />
                  </LField>
                </>
              )}
              {res.kind === 'labor' ? (
                <>
                  <LField label="Начисления на ФОТ, %">
                    <Input
                      inputMode="decimal"
                      value={rate.payrollPct}
                      onChange={(e) => setRate({ ...rate, payrollPct: e.target.value })}
                      className="max-md:h-11"
                    />
                  </LField>
                  <LField label="Вахтовые в день">
                    <Input
                      inputMode="decimal"
                      value={rate.rotationPerDay}
                      onChange={(e) => setRate({ ...rate, rotationPerDay: e.target.value })}
                      className="max-md:h-11"
                    />
                  </LField>
                  <LField label="Проживание в день">
                    <Input
                      inputMode="decimal"
                      value={rate.lodgingPerDay}
                      onChange={(e) => setRate({ ...rate, lodgingPerDay: e.target.value })}
                      className="max-md:h-11"
                    />
                  </LField>
                  <LField label="Питание в день">
                    <Input
                      inputMode="decimal"
                      value={rate.mealsPerDay}
                      onChange={(e) => setRate({ ...rate, mealsPerDay: e.target.value })}
                      className="max-md:h-11"
                    />
                  </LField>
                  <LField label="Дорога за заезд">
                    <Input
                      inputMode="decimal"
                      value={rate.travelPerTrip}
                      onChange={(e) => setRate({ ...rate, travelPerTrip: e.target.value })}
                      className="max-md:h-11"
                    />
                  </LField>
                  <LField label="СИЗ в месяц">
                    <Input
                      inputMode="decimal"
                      value={rate.ppePerMonth}
                      onChange={(e) => setRate({ ...rate, ppePerMonth: e.target.value })}
                      className="max-md:h-11"
                    />
                  </LField>
                </>
              ) : null}
              <div className="col-span-2 flex items-end sm:col-span-4">
                <Button type="submit" loading={addRate.isPending} className="max-md:h-11">
                  Сохранить новую версию ставок
                </Button>
              </div>
            </form>
          ) : null}
        </section>
      ) : null}
    </Dialog>
  );
}

/* ───────────────────────── Персонал ───────────────────────── */

function PeoplePanel({ projectId }: { projectId: string }) {
  const me = useMe();
  const q = useQuery({
    queryKey: ['workers', projectId || 'all'],
    queryFn: () => api<WorkerRow[]>('/workers', { query: { projectId: projectId || undefined } }),
  });
  const [open, setOpen] = useState<WorkerRow | 'new' | null>(null);
  const [filter, setFilter] = useState<'all' | 'problems'>('all');
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const canAdd = canCompanyWide(me.access, 'resources.edit') || Boolean(projectId);
  const rows = q.data.filter(
    (w) => filter === 'all' || w.expired.length || w.missing.length || w.expiring.length,
  );
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        {canAdd ? (
          <Button
            variant="primary"
            icon={<Plus className="size-4" />}
            onClick={() => setOpen('new')}
            className="max-md:h-11"
          >
            Новый сотрудник
          </Button>
        ) : null}
        <div className="w-[240px]">
          <Select
            value={filter}
            onChange={(e) => setFilter(e.target.value as typeof filter)}
            className="max-md:h-11"
            aria-label="Отбор"
          >
            <option value="all">Все</option>
            <option value="problems">С истёкшими и недостающими документами</option>
          </Select>
        </div>
      </div>
      {!rows.length ? (
        <EmptyState title="Сотрудников нет">
          Заведите сотрудников и рабочих подрядчиков с профессией, допусками и удостоверениями.
        </EmptyState>
      ) : (
        <Panel bodyClassName="p-0">
          <ul className="divide-y divide-line">
            {rows.map((w) => (
              <li key={w.id}>
                <button
                  type="button"
                  onClick={() => setOpen(w)}
                  className="flex min-h-[56px] w-full flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-left hover:bg-surface-2"
                >
                  <span className="min-w-0 flex-1">
                    <span className="text-[14px] font-medium">{w.fullName}</span>
                    {w.status === 'dismissed' ? <Badge className="ml-2">уволен</Badge> : null}
                    <span className="block text-[12px] text-ink-2">
                      {w.resourceName ?? 'профессия не указана'}
                      {w.counterpartyName ? ` · ${w.counterpartyName}` : ''}
                      {w.projectCode ? ` · ${w.projectCode}` : ''}
                    </span>
                  </span>
                  <span className="flex flex-wrap gap-1">
                    {w.expired.length ? <Badge tone="danger">истёк: {w.expired.join(', ')}</Badge> : null}
                    {w.missing.length ? <Badge tone="danger">нет: {w.missing.join(', ')}</Badge> : null}
                    {w.expiring.length ? (
                      <Badge tone="warning">истекает: {w.expiring.join(', ')}</Badge>
                    ) : null}
                    {w.outdated.length ? (
                      <Badge tone="warning">просрочен: {w.outdated.join(', ')}</Badge>
                    ) : null}
                    {!w.expired.length && !w.missing.length && !w.expiring.length && !w.outdated.length ? (
                      <Badge tone="success">документы в порядке</Badge>
                    ) : null}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Panel>
      )}
      {open ? (
        <WorkerDialog
          worker={open === 'new' ? null : open}
          projectId={projectId}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </div>
  );
}

function WorkerDialog({
  worker,
  projectId,
  onClose,
}: {
  worker: WorkerRow | null;
  projectId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const res = useQuery(resourcesQuery);
  const projects = useQuery(projectsQuery('active'));
  const [cur, setCur] = useState<WorkerRow | null>(worker);
  const [f, setF] = useState({
    fullName: worker?.fullName ?? '',
    personnelNo: worker?.personnelNo ?? '',
    resourceId: worker?.resourceId ?? '',
    projectId: worker?.projectId ?? projectId,
    phone: worker?.phone ?? '',
    status: worker?.status ?? 'active',
    userId: worker?.userId ?? '',
  });
  const people = useQuery({
    queryKey: ['resource-people'],
    queryFn: () => api<Array<{ id: string; name: string }>>('/resources/people'),
    retry: false,
  });
  const [doc, setDoc] = useState({ kind: 'permit', name: '', number: '', issuedOn: '', validUntil: '' });
  const done = (w: WorkerRow) => {
    setCur(w);
    qc.invalidateQueries({ queryKey: ['workers'] });
  };
  const save = useMutation({
    mutationFn: () => {
      const body = {
        fullName: f.fullName.trim(),
        personnelNo: f.personnelNo.trim() || null,
        resourceId: f.resourceId || null,
        projectId: f.projectId || null,
        phone: f.phone.trim() || null,
        status: f.status,
        ...(people.data ? { userId: f.userId || null } : {}),
      };
      return cur
        ? api<WorkerRow>(`/workers/${cur.id}`, { method: 'PATCH', body })
        : api<WorkerRow>('/workers', { body });
    },
    onSuccess: (w) => {
      done(w);
      toast('Сохранено');
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const addDoc = useMutation({
    mutationFn: () =>
      api<WorkerRow>(`/workers/${cur!.id}/docs`, {
        body: {
          kind: doc.kind,
          name: doc.name.trim(),
          number: doc.number.trim() || null,
          issuedOn: doc.issuedOn || null,
          validUntil: doc.validUntil || null,
        },
      }),
    onSuccess: (w) => {
      done(w);
      setDoc({ kind: 'permit', name: '', number: '', issuedOn: '', validUntil: '' });
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const delDoc = useMutation({
    mutationFn: async (id: string) => {
      await api(`/worker-docs/${id}`, { method: 'DELETE' });
      return (await api<WorkerRow[]>('/workers')).find((w) => w.id === cur!.id)!;
    },
    onSuccess: done,
    onError: (e) => toast(e.message, 'danger'),
  });
  const today = new Date().toISOString().slice(0, 10);
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      width="lg"
      title={cur ? cur.fullName : 'Новый сотрудник'}
      footer={
        <>
          <Button onClick={onClose} className="max-md:h-11">
            Закрыть
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={f.fullName.trim().length < 2}
            onClick={() => save.mutate()}
            className="max-md:h-11"
          >
            {cur ? 'Сохранить' : 'Добавить сотрудника'}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <LField label="ФИО">
          <Input
            value={f.fullName}
            onChange={(e) => setF({ ...f, fullName: e.target.value })}
            className="max-md:h-11"
          />
        </LField>
        <LField label="Табельный номер">
          <Input
            value={f.personnelNo}
            onChange={(e) => setF({ ...f, personnelNo: e.target.value })}
            className="max-md:h-11"
          />
        </LField>
        <LField label="Профессия и разряд">
          <Select
            value={f.resourceId}
            onChange={(e) => setF({ ...f, resourceId: e.target.value })}
            className="max-md:h-11"
          >
            <option value="">Не указана</option>
            {(res.data ?? [])
              .filter((r) => r.kind === 'labor')
              .map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                  {r.grade ? `, ${r.grade} разряд` : ''}
                </option>
              ))}
          </Select>
        </LField>
        <LField label="Проект">
          <Select
            value={f.projectId}
            onChange={(e) => setF({ ...f, projectId: e.target.value })}
            className="max-md:h-11"
          >
            <option value="">Без проекта</option>
            {(projects.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} {p.name}
              </option>
            ))}
          </Select>
        </LField>
        <LField label="Телефон">
          <Input
            value={f.phone}
            onChange={(e) => setF({ ...f, phone: e.target.value })}
            inputMode="tel"
            className="max-md:h-11"
          />
        </LField>
        {cur ? (
          <LField label="Состояние">
            <Select
              value={f.status}
              onChange={(e) => setF({ ...f, status: e.target.value as 'active' })}
              className="max-md:h-11"
            >
              <option value="active">Работает</option>
              <option value="dismissed">Уволен</option>
            </Select>
          </LField>
        ) : null}
        {people.data ? (
          <LField label="Сотрудник портала" hint="По нему видно, что за человеком числится на складе">
            <Select
              value={f.userId}
              onChange={(e) => setF({ ...f, userId: e.target.value })}
              className="max-md:h-11"
            >
              <option value="">Не связан</option>
              {people.data.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </LField>
        ) : null}
      </div>
      {cur?.userId ? <WorkerTools userId={cur.userId} /> : null}
      {cur ? (
        <section className="mt-6">
          <h3 className="mb-2 text-[14px] font-semibold">Допуски, удостоверения, медосмотры, инструктажи</h3>
          {cur.missing.length ? (
            <p className="mb-2 text-[13px] text-danger">По профессии нужны: {cur.missing.join(', ')}</p>
          ) : null}
          <ul className="mb-3 divide-y divide-line text-[14px]">
            {cur.docs.map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-2 py-1.5">
                <span>
                  <span className="text-ink-2">{WORKER_DOC_TITLES[d.kind]}:</span> {d.name}
                  {d.number ? ` № ${d.number}` : ''}
                  <span
                    className={clsx(
                      'ml-2 text-[13px]',
                      d.validUntil && d.validUntil < today ? 'text-danger' : 'text-ink-2',
                    )}
                  >
                    {d.validUntil ? `до ${d2(d.validUntil)}` : 'бессрочно'}
                  </span>
                </span>
                <IconButton
                  label="Удалить документ"
                  onClick={() => delDoc.mutate(d.id)}
                  className="size-11 md:size-9"
                >
                  <Trash2 className="size-4" />
                </IconButton>
              </li>
            ))}
          </ul>
          <form
            className="grid grid-cols-2 gap-3 sm:grid-cols-5"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              if (doc.name.trim()) addDoc.mutate();
            }}
          >
            <LField label="Вид">
              <Select
                value={doc.kind}
                onChange={(e) => setDoc({ ...doc, kind: e.target.value })}
                className="max-md:h-11"
              >
                {WORKER_DOC_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {WORKER_DOC_TITLES[k]}
                  </option>
                ))}
              </Select>
            </LField>
            <LField label="Название" className="col-span-2 sm:col-span-1">
              <Input
                value={doc.name}
                onChange={(e) => setDoc({ ...doc, name: e.target.value })}
                placeholder="Работы на высоте"
                className="max-md:h-11"
              />
            </LField>
            <LField label="Номер">
              <Input
                value={doc.number}
                onChange={(e) => setDoc({ ...doc, number: e.target.value })}
                className="max-md:h-11"
              />
            </LField>
            <LField label="Действует до">
              <Input
                type="date"
                value={doc.validUntil}
                onChange={(e) => setDoc({ ...doc, validUntil: e.target.value })}
                className="max-md:h-11"
              />
            </LField>
            <div className="flex items-end">
              <Button type="submit" loading={addDoc.isPending} className="max-md:h-11">
                Добавить
              </Button>
            </div>
          </form>
        </section>
      ) : null}
    </Dialog>
  );
}

/* ───────────────────────── Вахты ───────────────────────── */

function RotationsPanel({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const key = ['rotations', projectId];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api<RotationRow[]>(`/projects/${projectId}/rotations`),
  });
  const ws = useQuery({
    queryKey: ['workers', projectId],
    queryFn: () => api<WorkerRow[]>('/workers', { query: { projectId } }),
  });
  const [f, setF] = useState({ workerId: '', pattern: '15/15', planStart: '', planEnd: '' });
  const m = useMutation({
    mutationFn: (x: { url: string; method?: 'POST' | 'PATCH' | 'DELETE'; body?: unknown }) =>
      api<RotationRow[] | { ok: true }>(x.url, { method: x.method ?? 'POST', body: x.body }),
    onSuccess: (r) => {
      if (Array.isArray(r)) qc.setQueryData(key, r);
      else qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: ['resource-load'] });
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const len = (p: string) => Number(p.split('/')[0]) || 15;
  const add = (e: FormEvent) => {
    e.preventDefault();
    if (!f.workerId || !f.planStart) return;
    const end =
      f.planEnd ||
      new Date(new Date(`${f.planStart}T00:00:00Z`).getTime() + (len(f.pattern) - 1) * 86_400_000)
        .toISOString()
        .slice(0, 10);
    m.mutate({ url: `/projects/${projectId}/rotations`, body: { ...f, planEnd: end } });
    setF({ ...f, workerId: '', planEnd: '' });
  };
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="flex flex-col gap-4">
      {canEdit ? (
        <Panel title="Новый заезд">
          <form onSubmit={add} className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            <LField label="Сотрудник" className="col-span-2">
              <Select
                value={f.workerId}
                onChange={(e) => setF({ ...f, workerId: e.target.value })}
                className="max-md:h-11"
              >
                <option value="">Выберите…</option>
                {(ws.data ?? [])
                  .filter((w) => w.status === 'active')
                  .map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.fullName}
                      {w.resourceName ? ` · ${w.resourceName}` : ''}
                    </option>
                  ))}
              </Select>
            </LField>
            <LField label="График">
              <Select
                value={f.pattern}
                onChange={(e) => setF({ ...f, pattern: e.target.value })}
                className="max-md:h-11"
              >
                <option value="15/15">15/15</option>
                <option value="28/28">28/28</option>
                <option value="30/30">30/30</option>
              </Select>
            </LField>
            <LField label="Заезд">
              <Input
                type="date"
                value={f.planStart}
                onChange={(e) => setF({ ...f, planStart: e.target.value })}
                className="max-md:h-11"
              />
            </LField>
            <LField label="Выезд" hint="Пусто — по графику">
              <Input
                type="date"
                value={f.planEnd}
                onChange={(e) => setF({ ...f, planEnd: e.target.value })}
                className="max-md:h-11"
              />
            </LField>
            <div className="col-span-2 sm:col-span-5">
              <Button type="submit" variant="primary" className="max-md:h-11">
                Добавить заезд
              </Button>
            </div>
          </form>
        </Panel>
      ) : null}
      {!q.data.length ? (
        <EmptyState title="Вахт пока нет">
          Заезды и выезды по графику 15/15 или 28/28; факт заезда отмечается на площадке.
        </EmptyState>
      ) : (
        <Panel bodyClassName="p-0">
          <ul className="divide-y divide-line">
            {q.data.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5">
                <span className="min-w-0 flex-1">
                  <span className="text-[14px] font-medium">{r.workerName}</span>
                  <span className="block text-[12px] text-ink-2">
                    {r.pattern} · план {d2(r.planStart)} — {d2(r.planEnd)}
                    {r.actualStart ? ` · заехал ${d2(r.actualStart)}` : ''}
                    {r.actualEnd ? ` · выехал ${d2(r.actualEnd)}` : ''}
                  </span>
                </span>
                {r.overlap ? <Badge tone="danger">пересекается с другой вахтой</Badge> : null}
                {!r.actualStart && r.planStart < today ? (
                  <Badge tone="warning">заезд не отмечен</Badge>
                ) : null}
                {canEdit ? (
                  <span className="flex gap-1">
                    {!r.actualStart ? (
                      <Button
                        size="sm"
                        onClick={() =>
                          m.mutate({
                            url: `/rotations/${r.id}`,
                            method: 'PATCH',
                            body: { actualStart: today },
                          })
                        }
                        className="max-md:h-11"
                      >
                        Заехал сегодня
                      </Button>
                    ) : !r.actualEnd ? (
                      <Button
                        size="sm"
                        onClick={() =>
                          m.mutate({ url: `/rotations/${r.id}`, method: 'PATCH', body: { actualEnd: today } })
                        }
                        className="max-md:h-11"
                      >
                        Выехал сегодня
                      </Button>
                    ) : null}
                    <IconButton
                      label="Удалить вахту"
                      onClick={() =>
                        window.confirm('Удалить вахту?') &&
                        m.mutate({ url: `/rotations/${r.id}`, method: 'DELETE' })
                      }
                      className="size-11 md:size-9"
                    >
                      <Trash2 className="size-4" />
                    </IconButton>
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  );
}

/* ───────────────────────── Табель ───────────────────────── */

function TimesheetPanel({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const key = ['timesheet', projectId, month];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api<TimesheetView>(`/projects/${projectId}/timesheet`, { query: { month } }),
  });
  const [draft, setDraft] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: () => {
      const entries = Object.entries(draft).map(([k, v]) => {
        const [workerId, day] = k.split('|');
        return { workerId: workerId!, day: day!, hours: num(v) ?? 0 };
      });
      return api<TimesheetView>(`/projects/${projectId}/timesheet`, {
        method: 'PUT',
        query: { month },
        body: { entries },
      });
    },
    onSuccess: (v) => {
      qc.setQueryData(key, v);
      setDraft({});
      toast(v.approval?.stale ? 'Часы сохранены; согласование месяца устарело' : 'Часы сохранены');
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const approve = useMutation({
    mutationFn: () => api<TimesheetView>(`/projects/${projectId}/timesheet/approve`, { query: { month } }),
    onSuccess: (v) => {
      qc.setQueryData(key, v);
      toast('Табель согласован');
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const days = useMemo(() => {
    const [y, m] = month.split('-').map(Number);
    const n = new Date(Date.UTC(y!, m!, 0)).getUTCDate();
    return Array.from({ length: n }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`);
  }, [month]);
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const v = q.data;
  // Часы без привязки к работе — в общей строке дня
  const hours = (w: string, d: string) =>
    v.entries
      .filter((e) => e.workerId === w && e.day === d && !e.activityId)
      .reduce((s, e) => s + e.hours, 0);
  const withAct = (w: string, d: string) =>
    v.entries.filter((e) => e.workerId === w && e.day === d && e.activityId).reduce((s, e) => s + e.hours, 0);
  // Часы, которые пришли из принятого отчёта смены (РС-07), а не внесены вручную
  const fromReport = (w: string, d: string) =>
    v.entries
      .filter((e) => e.workerId === w && e.day === d && e.source === 'report')
      .reduce((s, e) => s + e.hours, 0);
  const reportTotal = v.entries.filter((e) => e.source === 'report').reduce((s, e) => s + e.hours, 0);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <LField label="Месяц">
          <Input
            type="month"
            value={month}
            onChange={(e) => {
              if (!e.target.value) return;
              if (
                Object.keys(draft).length &&
                !window.confirm('Несохранённые часы этого месяца пропадут. Перейти?')
              )
                return;
              setDraft({});
              setMonth(e.target.value);
            }}
            className="max-md:h-11"
          />
        </LField>
        {v.approval ? (
          v.approval.stale ? (
            <Badge tone="warning">
              согласование устарело: часы правили после {d2(v.approval.approvedAt)}
            </Badge>
          ) : (
            <Badge tone="success">
              согласован {v.approval.approvedBy}, {d2(v.approval.approvedAt)}
            </Badge>
          )
        ) : (
          <Badge>не согласован</Badge>
        )}
        {reportTotal ? (
          <Badge tone="info">
            из отчётов смены: {Math.round(reportTotal * 100) / 100} ч (доля — в подсказке ячейки)
          </Badge>
        ) : null}
        {v.canEdit && Object.keys(draft).length ? (
          <Button
            variant="primary"
            loading={save.isPending}
            onClick={() => save.mutate()}
            className="max-md:h-11"
          >
            Сохранить часы ({Object.keys(draft).length})
          </Button>
        ) : null}
        {v.canApprove && (!v.approval || v.approval.stale) ? (
          <Button loading={approve.isPending} onClick={() => approve.mutate()} className="max-md:h-11">
            Согласовать табель за месяц
          </Button>
        ) : null}
      </div>
      {!v.workers.length ? (
        <EmptyState title="В табеле никого нет">
          Люди появятся, когда их закрепят за проектом или заведут им вахту.
        </EmptyState>
      ) : (
        <Panel bodyClassName="p-0">
          <ScrollX>
            <table className="text-[12px]">
              <thead>
                <tr className="bg-surface-2 text-ink-2">
                  <th className="sticky left-0 z-10 min-w-[180px] bg-surface-2 py-2 pr-2 pl-4 text-left font-medium">
                    Сотрудник
                  </th>
                  {days.map((d) => (
                    <th key={d} className="num w-11 px-0.5 py-2 text-center font-medium">
                      {Number(d.slice(8))}
                    </th>
                  ))}
                  <th className="num px-2 py-2 text-right font-medium">Итого</th>
                </tr>
              </thead>
              <tbody>
                {v.workers.map((w) => {
                  const total = days.reduce(
                    (s, d) =>
                      s +
                      (draft[`${w.id}|${d}`] !== undefined
                        ? (num(draft[`${w.id}|${d}`]!) ?? 0)
                        : hours(w.id, d)) +
                      withAct(w.id, d),
                    0,
                  );
                  return (
                    <tr key={w.id} className="border-t border-line">
                      <td className="sticky left-0 z-10 bg-surface py-1 pr-2 pl-4">
                        <div className="max-w-[200px] truncate text-[13px]">{w.fullName}</div>
                        <div className="text-[11px] text-ink-2">{w.resourceName ?? ''}</div>
                      </td>
                      {days.map((d) => {
                        const k = `${w.id}|${d}`;
                        const val = draft[k] ?? (hours(w.id, d) ? String(hours(w.id, d)) : '');
                        const extra = withAct(w.id, d);
                        const rep = fromReport(w.id, d);
                        return (
                          <td key={d} className="px-0.5 py-1">
                            <input
                              aria-label={`${w.fullName}, ${d2(d)}`}
                              disabled={!v.canEdit}
                              inputMode="decimal"
                              value={val}
                              title={
                                extra
                                  ? `ещё ${extra} ч по работам графика${rep ? `, из них ${rep} ч из отчёта смены` : ''}`
                                  : undefined
                              }
                              onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                              className={clsx(
                                'num h-11 w-11 rounded-sm border border-line bg-surface text-center text-[12px] md:h-8 md:w-9',
                                extra && 'border-ink-2',
                              )}
                            />
                          </td>
                        );
                      })}
                      <td className="num px-2 py-1 text-right font-medium">{total}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </ScrollX>
        </Panel>
      )}
      {v.byActivity.length ? (
        <Panel title="План и факт чел.-ч по работам графика за месяц">
          <ul className="divide-y divide-line text-[13px]">
            {v.byActivity.map((a) => (
              <li key={a.activityId} className="flex justify-between gap-3 py-1.5">
                <span className="min-w-0 truncate">
                  {a.code} {a.name}
                </span>
                <span className={clsx('num shrink-0', a.fact > a.plan * 1.1 && 'text-danger')}>
                  план {a.plan} · факт {a.fact}
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}
    </div>
  );
}

/* ───────────────────────── Мобилизация ───────────────────────── */

function MobilizationPanel({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const key = ['mobilization', projectId];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api<MobilizationRow[]>(`/projects/${projectId}/mobilization`),
  });
  const res = useQuery(resourcesQuery);
  const load = useQuery({
    queryKey: ['resource-load', projectId],
    queryFn: () => api<LoadView>(`/projects/${projectId}/resource-load`),
  });
  const [f, setF] = useState({ resourceId: '', qty: '', needBy: '', kind: 'mobilize', note: '' });
  const m = useMutation({
    mutationFn: (x: { url: string; method?: 'POST' | 'PATCH' | 'DELETE'; body?: unknown }) =>
      api<MobilizationRow[] | { ok: true }>(x.url, { method: x.method ?? 'POST', body: x.body }),
    onSuccess: (r) => {
      if (Array.isArray(r)) qc.setQueryData(key, r);
      else qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: ['resource-load'] });
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  // Подсказки: где потребность больше наличия и плана — сколько добавить и к какой неделе
  const gaps = (load.data?.rows ?? [])
    .filter((r) => r.kind === 'labor' && r.available)
    .map((r) => {
      const i = r.need.findIndex((n, k) => n > (r.available![k] ?? 0) + (r.planned?.[k] ?? 0));
      if (i < 0) return null;
      const short = Math.ceil(
        Math.max(...r.need.map((n, k) => n - (r.available![k] ?? 0) - (r.planned?.[k] ?? 0))),
      );
      return { resourceId: r.resourceId, name: r.name, week: load.data!.weeks[i]!, short };
    })
    .filter((x): x is NonNullable<typeof x> => Boolean(x));
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const STATUS: Record<MobilizationRow['status'], string> = {
    planned: 'в плане',
    requested: 'заявка подана',
    done: 'на площадке',
    cancelled: 'отменено',
  };
  return (
    <div className="flex flex-col gap-4">
      {gaps.length ? (
        <Panel title="Не хватает людей по графику" meta="потребность больше, чем на вахте и в плане">
          <ul className="divide-y divide-line text-[14px]">
            {gaps.map((g) => (
              <li key={g.resourceId} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <span>
                  {g.name}: не хватает {g.short} с недели {d2(g.week)}
                </span>
                {canEdit ? (
                  <Button
                    size="sm"
                    onClick={() =>
                      m.mutate({
                        url: `/projects/${projectId}/mobilization`,
                        body: { resourceId: g.resourceId, qty: g.short, needBy: g.week, kind: 'mobilize' },
                      })
                    }
                    className="max-md:h-11"
                  >
                    Добавить в план
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}
      {canEdit ? (
        <Panel title="Новая строка плана">
          <form
            className="grid grid-cols-2 gap-3 sm:grid-cols-5"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              const qty = num(f.qty);
              if (!f.resourceId || !qty || !f.needBy) return;
              m.mutate({
                url: `/projects/${projectId}/mobilization`,
                body: { resourceId: f.resourceId, qty, needBy: f.needBy, kind: f.kind, note: f.note || null },
              });
              setF({ resourceId: '', qty: '', needBy: '', kind: 'mobilize', note: '' });
            }}
          >
            <LField label="Ресурс" className="col-span-2">
              <Select
                value={f.resourceId}
                onChange={(e) => setF({ ...f, resourceId: e.target.value })}
                className="max-md:h-11"
              >
                <option value="">Выберите…</option>
                {(res.data ?? [])
                  .filter((r) => r.kind !== 'material' && r.isActive)
                  .map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                      {r.grade ? `, ${r.grade} р.` : ''}
                    </option>
                  ))}
              </Select>
            </LField>
            <LField label="Сколько">
              <Input
                inputMode="decimal"
                value={f.qty}
                onChange={(e) => setF({ ...f, qty: e.target.value })}
                className="max-md:h-11"
              />
            </LField>
            <LField label="К дате">
              <Input
                type="date"
                value={f.needBy}
                onChange={(e) => setF({ ...f, needBy: e.target.value })}
                className="max-md:h-11"
              />
            </LField>
            <LField label="Как">
              <Select
                value={f.kind}
                onChange={(e) => setF({ ...f, kind: e.target.value })}
                className="max-md:h-11"
              >
                <option value="mobilize">Мобилизация своих</option>
                <option value="hire">Подбор новых</option>
              </Select>
            </LField>
            <div className="col-span-2 sm:col-span-5">
              <Button type="submit" variant="primary" className="max-md:h-11">
                Добавить в план
              </Button>
            </div>
          </form>
        </Panel>
      ) : null}
      {!q.data.length ? (
        <EmptyState title="План мобилизации пуст">
          Строки добавляются вручную или по подсказке «не хватает людей по графику».
        </EmptyState>
      ) : (
        <Panel bodyClassName="p-0">
          <ul className="divide-y divide-line">
            {q.data.map((x) => (
              <li key={x.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5">
                <span className="min-w-0 flex-1">
                  <span className="text-[14px] font-medium">
                    {x.resourceName}: {x.qty}
                  </span>
                  <span className="block text-[12px] text-ink-2">
                    {x.kind === 'hire' ? 'подбор' : 'мобилизация'} к {d2(x.needBy)}
                    {x.activityName ? ` · к работе «${x.activityName}»` : ''}
                  </span>
                </span>
                {canEdit ? (
                  <div className="flex items-center gap-1">
                    <div className="w-[160px]">
                      <Select
                        value={x.status}
                        onChange={(e) =>
                          m.mutate({
                            url: `/mobilization/${x.id}`,
                            method: 'PATCH',
                            body: { status: e.target.value },
                          })
                        }
                        className="max-md:h-11"
                        aria-label="Состояние"
                      >
                        {Object.entries(STATUS).map(([k, t]) => (
                          <option key={k} value={k}>
                            {t}
                          </option>
                        ))}
                      </Select>
                    </div>
                    <IconButton
                      label="Удалить строку"
                      onClick={() => m.mutate({ url: `/mobilization/${x.id}`, method: 'DELETE' })}
                      className="size-11 md:size-9"
                    >
                      <Trash2 className="size-4" />
                    </IconButton>
                  </div>
                ) : (
                  <Badge>{STATUS[x.status]}</Badge>
                )}
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  );
}

/** Инструмент и средства измерений за сотрудником (РС-13): данные склада (раздел 6.13), только чтение */
function WorkerTools({ userId }: { userId: string }) {
  const q = useQuery({
    queryKey: ['worker-tools', userId],
    queryFn: () => api<ToolUnit[]>('/warehouse/tools', { query: { holderUserId: userId } }),
    retry: false,
  });
  if (!q.data) return null;
  return (
    <section className="mt-6">
      <h3 className="mb-2 text-[14px] font-semibold">
        Числится за сотрудником: инструмент и средства измерений
      </h3>
      {q.data.length ? (
        <ul className="divide-y divide-line text-[14px]">
          {q.data.map((t) => (
            <li key={t.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
              <Link to="/warehouse" search={{ tab: 'tools', poa: undefined }} className="hover:underline">
                {t.itemName} · инв. № {t.inventoryNumber}
              </Link>
              <span className="flex flex-wrap gap-1 text-[12px]">
                {t.returnBy ? (
                  <Badge tone={t.returnOverdue ? 'danger' : undefined}>
                    вернуть до {t.returnBy.split('-').reverse().join('.')}
                  </Badge>
                ) : null}
                {t.checkState === 'overdue' || t.checkState === 'soon' ? (
                  <Badge tone={t.checkState === 'overdue' ? 'danger' : 'warning'}>
                    поверка {t.nextCheckOn?.split('-').reverse().join('.') ?? ''}
                  </Badge>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[13px] text-ink-2">Ничего не числится.</p>
      )}
    </section>
  );
}
